import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'
import { USER_UPDATED_EVENT } from '../../helpers/UserStorage'

interface AuthProviderProps {
  children: ReactNode
}

export interface AuthUser {
  nome?: string
  email?: string
  username?: string
  permissions?: string[]
  [key: string]: any
}

interface AuthContextType {
  userCan: (permission: string) => boolean
  user: AuthUser | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function getUser (): AuthUser | null {
  try {
    const raw = localStorage.getItem('user')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function AuthProvider ({ children }: AuthProviderProps): React.ReactElement {
  const [user, setUser] = useState<AuthUser | null>(() => getUser())

  useEffect(() => {
    const handleUserUpdated = (): void => setUser(getUser())
    window.addEventListener(USER_UPDATED_EVENT, handleUserUpdated)
    window.addEventListener('storage', handleUserUpdated)

    return () => {
      window.removeEventListener(USER_UPDATED_EVENT, handleUserUpdated)
      window.removeEventListener('storage', handleUserUpdated)
    }
  }, [])

  const userCan = useCallback((permission: string) => {
    const permissions = user?.permissions
    if (!permissions) return false
    return permissions.includes(permission)
  }, [user])

  return <AuthContext.Provider value={{ userCan, user }}>
    {children}
  </AuthContext.Provider>
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider')
  }
  return context
}

export default AuthProvider
